import * as Y from '../../../../../shared/yjs';
import { EditorView } from 'prosemirror-view';
import { createFileLogger } from '../../../../../utils/logger';
import { nanoid } from 'nanoid';
import { SafeYJSExtraction } from '../../../../../shared/utils/yjsTextExtraction';
import { schema } from '../../../editor/schema';


const log = createFileLogger('Y_DOC_LOW_LEVEL_API', { enabled: false });

// Frequency reduction for Y doc operation logs
const logFrequencyManager = {
  nodeSearchCount: 0,
  elementCreationCount: 0,
  fragmentAccessCount: 0,
  
  // Log every Nth operation to reduce spam
  SAMPLE_RATES: {
    NODE_SEARCH: 50,     // Log every 50th node search
    ELEMENT_CREATION: 20, // Log every 20th element creation
    FRAGMENT_ACCESS: 10   // Log every 10th fragment access
  },
  
  shouldLogNodeSearch(): boolean {
    this.nodeSearchCount++;
    return this.nodeSearchCount % this.SAMPLE_RATES.NODE_SEARCH === 0;
  },
  
  shouldLogElementCreation(): boolean {
    this.elementCreationCount++;
    return this.elementCreationCount % this.SAMPLE_RATES.ELEMENT_CREATION === 0;
  },
  
  shouldLogFragmentAccess(): boolean {
    this.fragmentAccessCount++;
    return this.fragmentAccessCount % this.SAMPLE_RATES.FRAGMENT_ACCESS === 0;
  }
};

// Use centralized abstract pattern ID generation for consistency
type IdStrategy = 'nanoid' | 'friendly';
// Dev flag: set to 'friendly' for node_{noun}_{noun}_{noun}, or 'nanoid' for compact ids
export const NODE_ID_STRATEGY: IdStrategy = 'friendly';

const friendlyNouns = [
  'river','forest','orchid','granite','ember','aurora','canyon','harbor','meadow','willow',
  'compass','beacon','matrix','vertex','delta','vector','atlas','summit','quartz','nebula',
  'fox','sparrow','otter','lynx','falcon','whale','coral','cedar','birch','maple',
  'sage','pine','elm','ash','spruce','ivy','bamboo','lotus','sagebrush',
  'eagle','heron','swift','wolf','badger','beaver','manta','corvus','stallion','mare',
  'granary','forge','haven','oasis','mesa','ridge','fjord','glacier','grotto',
  'crystal','prism','onyx','jade','ruby','pearl','amber','obsidian','topaz','garnet',
  'thunder','lightning','storm','breeze','mist','frost','snow','rain','cloud','wind',
  'phoenix','dragon','griffin','raven','dove','hawk','owl','robin','wren','finch',
  'ocean','lake','stream','brook','pond','bay','cove','inlet','strait','channel',
  'mountain','hill','valley','plain','desert','tundra','prairie','savanna','jungle','swamp',
  'castle','tower','bridge','gate','arch','dome','spire','pillar','vault','chamber',
  'anchor','window','lantern','geode','bonfire','citadel','bastion','sanctuary','temple','shrine'
];

// function randomFrom<T>(arr: T[]): T { return arr[Math.floor(Math.random() * arr.length)]; }

function generateFriendlyNodeId(): string {
  // Pick three distinct nouns for higher uniqueness without suffixes
  const pool = [...friendlyNouns];
  const a = pool.splice(Math.floor(Math.random() * pool.length), 1)[0];
  const b = pool.splice(Math.floor(Math.random() * pool.length), 1)[0];
  const c = pool.splice(Math.floor(Math.random() * pool.length), 1)[0];
  return `node_${a}_${b}_${c}`;
}

// Normalize document id for safe prefixing in node ids
// (no doc prefixing for node ids)

export function generateLLMId(type: 'node' | 'window' | 'document' = 'node', _docId?: string): string {
  const strategy = NODE_ID_STRATEGY;
  if (type === 'node') {
    // Always start with node_ and never prefix with document id
    const tail = strategy === 'friendly' ? generateFriendlyNodeId().replace(/^node_/, '') : nanoid(12);
    return `node_${tail}`;
  }
  // Non-node ids keep their type prefix; no doc id prefixing
  const tail = strategy === 'friendly' ? generateFriendlyNodeId().slice(5) : nanoid(12);
  return `${type}_${tail}`;
}

// Helper function to count words in text
const countWords = (text: string): number => {
  if (!text || typeof text !== 'string') return 0;
  // Split by whitespace and filter out empty strings
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
};

// Types for document structure
export interface YDocumentNode {
  id: string;
  type: string;
  text: string;
  wordCount: number; // ✅ NEW: Word count for content length expectations
  changeStatus: 'new' | 'old' | 'none';
  revisionId?: string;
  index: number;
  parentPageId?: string; // ✅ NEW: Track which page this node belongs to for hierarchical reading
  attributes?: Record<string, any>;
}

export interface YDocumentStructure {
  nodes: YDocumentNode[];
  totalNodes: number;
  nodesInContext: number;
  hasMoreNodes: boolean;
  documentStats: {
    totalCharacters: number;
    totalWordCount: number; // ✅ NEW: Total words in entire document
    contextWordCount: number; // ✅ NEW: Words in nodes provided in context
    averageWordsPerNode: number; // ✅ NEW: Average words per node for content depth guidance
    nodeTypes: string[];
    hasChangeTracking: boolean;
  };
}

export interface YDocumentAccessOptions {
  windowId?: string;
  docId?: string;
  logPrefix?: string;
}

// Centralized Undo origins for Yjs transactions
// Use these objects as the `origin` in ydoc.transact(..., origin)
export const changeDecorationOrigin = { name: 'DocsApp.changeDecoration' } as const;
export const yDocLLMApiOrigin = { name: 'DocsApp.yDocLLMAPI' } as const;
export const yDocHighLevelApiOrigin = { name: 'DocsApp.yDocHighLevelAPI' } as const;

/**
 * ✅ CENTRALIZED Y.js Element Factory
 * This ensures ALL Y.js elements get proper IDs automatically
 */
export function createYElement(
  type: string, 
  options: {
    id?: string;
    attributes?: Record<string, any>;
    content?: string;
    marks?: Array<{ type: string; attrs?: Record<string, any> }>;
    logPrefix?: string;
    docId?: string;
  } = {}
): Y.XmlElement {
  const logPrefix = options.logPrefix || '[YElementFactory]';
  
  // ✅ FIX: Ensure type is valid, fallback to 'paragraph' if undefined/null
  const validType = type || 'paragraph';
  
  // Create the Y.js element
  const element = new Y.XmlElement(validType);
  
  // Always assign a proper LLMId with a stable `node_` prefix (no doc-id prefixing)
  const elementId = options.id || generateLLMId('node', options.docId);
  element.setAttribute('id', elementId);
  element.setAttribute('type', validType);
  
  // Set any additional attributes
  if (options.attributes) {
    Object.entries(options.attributes).forEach(([key, value]) => {
      element.setAttribute(key, value);
    });
  }
  
  // Add text content if provided
  if (options.content) {
    const textContent = options.content;
    const textNode = new Y.XmlText();
    // Insert text first
    textNode.insert(0, textContent);
    
    // Apply marks if provided
    if (options.marks && options.marks.length > 0) {
      options.marks.forEach(mark => {
        const markAttrs: any = { [mark.type]: true };
        if (mark.attrs) {
          Object.assign(markAttrs, mark.attrs);
        }
        textNode.format(0, textContent.length, markAttrs);
      });
    }
    
    // REFACTORED: UniversalSchema now uses 2-level structure
    // Direct text insertion (paragraph → text, heading → text)
    element.insert(0, [textNode]);
  }
  
  if (logFrequencyManager.shouldLogElementCreation()) {
    log.log(`${logPrefix} Created Y.js element (showing every ${logFrequencyManager.SAMPLE_RATES.ELEMENT_CREATION}th creation):`, {
      type: validType,
      originalType: type,
      id: elementId,
      hasContent: !!options.content,
      hasMarks: !!(options.marks && options.marks.length > 0),
      attributeCount: Object.keys(options.attributes || {}).length
    });
  }
  
  return element;
}

/**
 * Centralized Y.js document service for consistent access patterns
 */
export class YDocumentService {
  private static instance: YDocumentService
  
  private nodeCache: Map<string, { element: Y.XmlElement; index: number; parent: Y.XmlFragment | Y.XmlElement; timestamp: number }> = new Map()
  private readonly CACHE_TTL = 30000 // 30 seconds cache;
  private logPrefix: string;

  private constructor() {
    this.logPrefix = '[YDocumentService]';
  }

  public static getInstance(): YDocumentService {
    if (!YDocumentService.instance) {
      YDocumentService.instance = new YDocumentService();
    }
    return YDocumentService.instance;
  }

  /**
   * Factory method for creating Y.js elements with proper IDs
   * Use this instead of 'new Y.XmlElement()' to ensure consistent ID assignment
   */
  public createElement(
    type: string, 
    options: {
      id?: string;
      attributes?: Record<string, any>;
      content?: string;
    } = {}
  ): Y.XmlElement {
    // Honor caller-supplied id if provided; otherwise generate
    return createYElement(type, {
      ...options,
      logPrefix: this.logPrefix
    });
  }

  public createYjsNode(contentItem: any): Y.XmlElement {
    const { type, text, attributes } = contentItem;
    const element = new Y.XmlElement(type || 'paragraph');

    if (attributes) {
      Object.entries(attributes).forEach(([key, value]) => {
        element.setAttribute(key, value as string);
      });
    }

    if (text) {
      const yText = new Y.XmlText();
      // 🔧 DECODE HTML ENTITIES: Safety net before Y.js insertion
      const decodedText = text; // Using text directly - entities fixed at proper boundary
      yText.insert(0, decodedText);
      element.insert(0, [yText]);
    }

    return element;
  }

  /**
   * Safely get XML fragment from Y.js document
   */
  public getXmlFragment(
    ydoc: Y.Doc | null, 
    fragmentName: string = 'prosemirror',
    options: YDocumentAccessOptions = {}
  ): Y.XmlFragment | null {
    const logPrefix = options.logPrefix || this.logPrefix;
    
    try {
      if (!ydoc) {
        log.warn(`${logPrefix} No Y.js document provided`);
        return null;
      }

      const fragment = ydoc.getXmlFragment(fragmentName);
      // Only log fragment access if there are issues or with sampling for normal operations
      // if (fragment.length === 0) {
      //   log.warn(`${logPrefix} Retrieved empty XML fragment '${fragmentName}'`, {
      //     windowId: options.windowId,
      //     docId: options.docId
      //   });
      // } else if (logFrequencyManager.shouldLogFragmentAccess()) {
      //   log.log(`${logPrefix} Retrieved XML fragment '${fragmentName}' with ${fragment.length} elements (showing every ${logFrequencyManager.SAMPLE_RATES.FRAGMENT_ACCESS}th access)`, {
      //     windowId: options.windowId,
      //     docId: options.docId,
      //     fragmentLength: fragment.length
      //   });
      // }

      return fragment;
    } catch (error) {
      log.error(`${logPrefix} Error accessing XML fragment '${fragmentName}':`, error);
      return null;
    }
  }

  /**
   * Extract text content from Y.js XML element using safe ProseMirror pipeline
   * REPLACES: dangerous toString() usage with proper YJS → PM → Text conversion
   */
  public extractTextContent(element: Y.XmlElement | Y.XmlFragment): string {
    try {
      // 🚨 CRITICAL FIX: Use safe ProseMirror extraction instead of toString()
      // This eliminates XML contamination at the source
      
      // For individual elements, we need to create a temporary document
      // to use the ProseMirror extraction pipeline
      if (element && element.constructor && element.constructor.name === 'XmlFragment') {
        // Fragment case: can extract directly
        const result = SafeYJSExtraction.extractContent(
          element.doc as Y.Doc, 
          schema, 
          { 
            fragmentKey: 'prosemirror',
            filterForAI: false // Don't filter here, let caller decide
          }
        );
        
        if (result.success && result.plainText) {

          return result.plainText;
        }
      } else if (element && typeof element.length === 'number' && typeof element.get === 'function') {
        // Element case: extract from individual element using safe methods
        // Use duck typing instead of constructor.name for better compatibility
        let text = '';
        
        for (let i = 0; i < element.length; i++) {
          const child = element.get(i);
          if (child) {
            // Check if child is Y.XmlText by looking for toDelta method
            if (child && typeof (child as any).toDelta === 'function') {
              // ✅ SAFE: Use YJS Text toDelta() method for Y.XmlText
              // This is safe because Y.XmlText.toDelta() returns clean text operations
              try {
                const delta = (child as any).toDelta();
                const childText = delta.map((op: any) => op.insert || '').join('');
                text += childText;
                

              } catch (deltaError) {
                console.warn('[yDocLowLevelAPI] ⚠️ Delta extraction failed, using boundary cleanup:', deltaError);
                // Last resort: use toString() but apply boundary cleanup
                const rawText = child.toString();
                text += SafeYJSExtraction.boundaryCleanup(rawText);
              }
            } else if (child && typeof child.length === 'number' && typeof (child as any).get === 'function') {
              // Recursively extract from child elements (duck typing for Y.XmlElement)
              text += this.extractTextContent(child as any);
            }
          }
        }
        
        return text;
      }
      
      // Fallback: try to extract from any element type
      console.warn('[yDocLowLevelAPI] ⚠️ Unknown element type, trying generic extraction:', {
        elementType: element?.constructor?.name,
        hasLength: typeof element?.length === 'number',
        hasGet: typeof element?.get === 'function'
      });
      
      // Try generic extraction for unknown element types
      if (element && typeof element.length === 'number' && typeof element.get === 'function') {
        let text = '';
        for (let i = 0; i < element.length; i++) {
          const child = element.get(i);
          if (child) {
            // Try toDelta for any child that supports it
            if (typeof (child as any).toDelta === 'function') {
              try {
                const delta = (child as any).toDelta();
                text += delta.map((op: any) => op.insert || '').join('');
              } catch (deltaError) {
                // Last resort with boundary cleanup
                if (typeof child.toString === 'function') {
                  const rawText = child.toString();
                  text += SafeYJSExtraction.boundaryCleanup(rawText);
                }
              }
            } else if (typeof child.toString === 'function') {
              // Direct toString with cleanup
              const rawText = child.toString();
              text += SafeYJSExtraction.boundaryCleanup(rawText);
            }
          }
        }
        return text;
      }
      
      return '';
      
    } catch (error) {
      console.error('[yDocLowLevelAPI] CRITICAL: Safe text extraction failed:', error);
      return '';
    }
  }

  /**
   * Get structured document data from Y.js fragment
   */
  public getDocumentStructure(
    ydoc: Y.Doc | null,
    maxNodes: number = 15,
    options: YDocumentAccessOptions = {}
  ): YDocumentStructure | null {
    const logPrefix = options.logPrefix || this.logPrefix;
    
    try {
      const yxml = this.getXmlFragment(ydoc, 'prosemirror', options);
      if (!yxml) {
        log.warn(`${logPrefix} No XML fragment available for document structure`);
        return null;
      }

      // Extract nodes with real IDs and structure
      const allNodes: YDocumentNode[] = [];
      
      for (let i = 0; i < yxml.length; i++) {
        const element = yxml.get(i);
        if (element && element instanceof Y.XmlElement) {
          const nodeId = element.getAttribute('id');
          const nodeType = element.getAttribute('type') || element.nodeName;
          const changeStatus = element.getAttribute('change_status') || 'none';
          const revisionId = element.getAttribute('revision_id');
          
          // Get text content
          let text = '';
          try {
            text = this.extractTextContent(element);
          } catch (error) {
            log.warn(`${logPrefix} Error extracting text from node ${nodeId}:`, error);
          }
          
          if (nodeId) {
            // ✅ Calculate word count for AI content length expectations
            const wordCount = countWords(text);
            
            allNodes.push({
              id: nodeId,
              type: nodeType,
              text: text,
              wordCount: wordCount, // ✅ NEW: Word count for content length expectations
              changeStatus: changeStatus as 'new' | 'old' | 'none',
              revisionId: revisionId !== undefined ? revisionId : undefined,
              index: i,
              attributes: element.getAttributes ? element.getAttributes() : undefined
            });
            
            // ✅ NEW: If this is a page, also process its children for hierarchical reading
            if (element.nodeName === 'page') {
              for (let j = 0; j < element.length; j++) {
                const pageChild = element.get(j);
                if (pageChild instanceof Y.XmlElement) {
                  const childId = pageChild.getAttribute('id');
                  const childType = pageChild.getAttribute('type') || pageChild.nodeName;
                  const childChangeStatus = pageChild.getAttribute('change_status') || 'none';
                  const childRevisionId = pageChild.getAttribute('revision_id');
                  
                  let childText = '';
                  try {
                    childText = this.extractTextContent(pageChild);
                  } catch (error) {
                    log.warn(`${logPrefix} Error extracting text from page child ${childId}:`, error);
                  }
                  
                  if (childId) {
                    allNodes.push({
                      id: childId,
                      type: childType,
                      text: childText,
                      wordCount: countWords(childText),
                      changeStatus: childChangeStatus as 'new' | 'old' | 'none',
                      revisionId: childRevisionId !== undefined ? childRevisionId : undefined,
                      index: j, // Index within page
                      parentPageId: nodeId, // ✅ Track which page this belongs to
                      attributes: pageChild.getAttributes ? pageChild.getAttributes() : undefined
                    });
                  }
                }
              }
            }
          }
        }
      }

      // Limit nodes but provide metadata about full document
      const limitedNodes = allNodes.slice(0, maxNodes);
      const hasMoreNodes = allNodes.length > maxNodes;

      const documentStructure: YDocumentStructure = {
        nodes: limitedNodes,
        totalNodes: allNodes.length,
        nodesInContext: limitedNodes.length,
        hasMoreNodes: hasMoreNodes,
        documentStats: {
          totalCharacters: allNodes.reduce((sum, node) => sum + node.text.length, 0),
          totalWordCount: allNodes.reduce((sum, node) => sum + node.wordCount, 0), // ✅ NEW: Total words in document
          contextWordCount: limitedNodes.reduce((sum, node) => sum + node.wordCount, 0), // ✅ NEW: Words in context
          averageWordsPerNode: allNodes.length > 0 ? Math.round(allNodes.reduce((sum, node) => sum + node.wordCount, 0) / allNodes.length) : 0, // ✅ NEW: Avg words per node
          nodeTypes: [...new Set(allNodes.map(node => node.type))],
          hasChangeTracking: allNodes.some(node => node.changeStatus !== 'none')
        }
      };

      // log.log(`${logPrefix} ✅ Extracted structured document data:`, {
      //   totalNodes: allNodes.length,
      //   nodesInContext: limitedNodes.length,
      //   hasMoreNodes,
      //   windowId: options.windowId,
      //   docId: options.docId
      // });

      return documentStructure;
    } catch (error) {
      log.error(`${logPrefix} ❌ Error extracting document structure:`, error);
      return null;
    }
  }

  /**
   * Get preview text from Y.js document
   */
  public getPreviewText(
    ydoc: Y.Doc | null,
    maxCharacters: number = 500,
    options: YDocumentAccessOptions = {}
  ): string {
    const logPrefix = options.logPrefix || this.logPrefix;
    
    try {
      const yxml = this.getXmlFragment(ydoc, 'prosemirror', options);
      if (!yxml) {
        log.warn(`${logPrefix} No XML fragment available for preview text`);
        return '';
      }

      const fullText = this.extractTextContent(yxml);
      const previewText = fullText.length > maxCharacters 
        ? fullText.substring(0, maxCharacters) + '...'
        : fullText;

      log.log(`${logPrefix} Generated preview text (${previewText.length}/${fullText.length} chars)`, {
        windowId: options.windowId,
        docId: options.docId
      });

      return previewText;
    } catch (error) {
      log.error(`${logPrefix} Error generating preview text:`, error);
      return '';
    }
  }

  /**
   * Find a node by ID in the Y.js document with caching
   */
  public findNodeById(
    ydoc: Y.Doc | null,
    nodeId: string,
    options: YDocumentAccessOptions & { strict?: boolean } = {}
  ): { element: Y.XmlElement; index: number; parent: Y.XmlFragment | Y.XmlElement } | null {
    const logPrefix = options.logPrefix || this.logPrefix;
    
    try {
      // ✅ OPTIMIZATION: Check cache first
      const cacheKey = `${ydoc?.guid || 'unknown'}_${nodeId}`;
      const cached = this.nodeCache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < this.CACHE_TTL) {
        // Verify the cached element is still valid
        try {
          if (cached.element.getAttribute('id') === nodeId) {
            return cached;
          }
        } catch {
          // Element might be invalid, remove from cache
          this.nodeCache.delete(cacheKey);
        }
      }

      const yxml = this.getXmlFragment(ydoc, 'prosemirror', options);
      if (!yxml) {
        log.warn(`${logPrefix} No XML fragment available for node search`);
        return null;
      }

      const result = this.findNodeByIdRecursive(yxml, nodeId, logPrefix, !!options.strict);
      
      // ✅ OPTIMIZATION: Cache successful results
      if (result) {
        this.nodeCache.set(cacheKey, {
          ...result,
          timestamp: Date.now()
        });
      }
      
      return result;
    } catch (error) {
      log.error(`${logPrefix} Error finding node by ID ${nodeId}:`, error);
      return null;
    }
  }

  /**
   * Optimized helper for finding nodes by ID with breadth-first search
   */
  private findNodeByIdRecursive(
    container: Y.XmlFragment | Y.XmlElement,
    nodeId: string,
    logPrefix: string,
    strict: boolean
  ): { element: Y.XmlElement; index: number; parent: Y.XmlFragment | Y.XmlElement } | null {
    
    const isTopLevel = container instanceof Y.XmlFragment;
    
    // Fast breadth-first search at current level
    for (let i = 0; i < container.length; i++) {
      const element = container.get(i);
      
      if (element instanceof Y.XmlElement) {
        const elementId = element.getAttribute('id');
        
        if (elementId === nodeId) {
          return { element, index: i, parent: container };
        }
      }
    }
    
    // ✅ PHASE 2: Only search nested elements if not found at current level and not strict mode
    if (!strict) {
      for (let i = 0; i < container.length; i++) {
        const element = container.get(i);
        
        if (element instanceof Y.XmlElement && element.length > 0) {
          const childResult = this.findNodeByIdRecursive(element, nodeId, logPrefix, strict);
          if (childResult) {
            return childResult;
          }
        }
      }
    }
    
    // ✅ OPTIMIZATION: Only log failures at top level to reduce noise
    if (isTopLevel) {
      const foundElementIds: string[] = [];
      for (let i = 0; i < container.length; i++) {
        const element = container.get(i);
        if (element instanceof Y.XmlElement) {
          const elementId = element.getAttribute('id');
          if (elementId) foundElementIds.push(elementId);
        }
      }
      
      log.log(`🔍 [NODE_SEARCH_DEBUG] ❌ NODE NOT FOUND in container:`, {
        searchingFor: nodeId,
        containerType: 'fragment',
        containerLength: container.length,
        allFoundElements: foundElementIds.length,
        foundElementIds: foundElementIds.slice(0, 10) // Limit to first 10 for readability
      });
    }
    
    return null;
  }

  /**
   * Clear node cache (call when document is modified)
   */
  public clearNodeCache(docGuid?: string): void {
    if (docGuid) {
      // Clear cache entries for specific document
      const keysToDelete: string[] = [];
      for (const key of this.nodeCache.keys()) {
        if (key.startsWith(`${docGuid}_`)) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(key => this.nodeCache.delete(key));
    } else {
      // Clear entire cache
      this.nodeCache.clear();
    }
  }

  /**
   * Get document stats from Y.js document
   */
  public getDocumentStats(
    ydoc: Y.Doc | null,
    options: YDocumentAccessOptions = {}
  ): {
    totalNodes: number;
    nodesByType: Record<string, number>;
    totalCharacters: number;
    totalWords: number;
  } | null {
    const logPrefix = options.logPrefix || this.logPrefix;
    
    try {
      const yxml = this.getXmlFragment(ydoc, 'prosemirror', options);
      if (!yxml) {
        log.warn(`${logPrefix} No XML fragment available for document stats`);
        return null;
      }

      const stats = {
        totalNodes: 0,
        nodesByType: {} as Record<string, number>,
        totalCharacters: 0,
        totalWords: 0
      };

      const analyzeFragment = (fragment: Y.XmlFragment | Y.XmlElement) => {
        for (let i = 0; i < fragment.length; i++) {
          const child = fragment.get(i);
          if (child instanceof Y.XmlElement) {
            const nodeType = child.getAttribute('type') || child.nodeName;
            stats.totalNodes++;
            stats.nodesByType[nodeType] = (stats.nodesByType[nodeType] || 0) + 1;
            
            const text = this.extractTextContent(child);
            stats.totalCharacters += text.length;
            stats.totalWords += text.split(/\s+/).filter(word => word.length > 0).length;
            
            // Recursively analyze children
            analyzeFragment(child);
          }
        }
      };

      analyzeFragment(yxml);

      log.log(`${logPrefix} Document stats calculated:`, {
        ...stats,
        windowId: options.windowId,
        docId: options.docId
      });

      return stats;
    } catch (error) {
      log.error(`${logPrefix} Error calculating document stats:`, error);
      return null;
    }
  }

  /**
   * Migrate document to ensure all elements have proper IDs
   * This fixes existing documents that have nodes with id="undefined"
   */
  public migrateDocumentIds(
    ydoc: Y.Doc | null,
    options: YDocumentAccessOptions = {}
  ): { migrated: boolean; nodesFixed: number } {
    const logPrefix = options.logPrefix || this.logPrefix;
    
    if (!ydoc) {
      return { migrated: false, nodesFixed: 0 };
    }

    try {
      const yxml = this.getXmlFragment(ydoc, 'prosemirror', options);
      if (!yxml) {
        return { migrated: false, nodesFixed: 0 };
      }

      let nodesFixed = 0;
      const fixElementIds = (container: Y.XmlFragment | Y.XmlElement) => {
        for (let i = 0; i < container.length; i++) {
          const child = container.get(i);
          if (child instanceof Y.XmlElement) {
            const currentId = child.getAttribute('id');
            const currentType = child.getAttribute('type');
            
            // Fix missing or undefined IDs
            if (!currentId || currentId === 'undefined') {
              const newId = generateLLMId('node', options.docId);
              child.setAttribute('id', newId);
              nodesFixed++;
              log.log(`${logPrefix} Fixed node ID: undefined -> ${newId}`);
            }
            
            // Fix missing or undefined types
            if (!currentType || currentType === 'undefined') {
              const nodeType = child.nodeName || 'paragraph';
              child.setAttribute('type', nodeType);
              log.log(`${logPrefix} Fixed node type: undefined -> ${nodeType}`);
            }
            
            // Recursively fix child elements
            fixElementIds(child);
          }
        }
      };

      // Perform migration in a transaction
      ydoc.transact(() => {
        fixElementIds(yxml);
      });

      if (nodesFixed > 0) {
        log.log(`${logPrefix} Migration completed: Fixed ${nodesFixed} nodes with missing IDs`, {
          windowId: options.windowId,
          docId: options.docId
        });
      }

      return { migrated: nodesFixed > 0, nodesFixed };
    } catch (error) {
      log.error(`${logPrefix} Error during ID migration:`, error);
      return { migrated: false, nodesFixed: 0 };
    }
  }

  /**
   * Validate Y.js document access
   */
  public validateDocument(
    ydoc: Y.Doc | null,
    options: YDocumentAccessOptions = {}
  ): { valid: boolean; message: string } {
    const logPrefix = options.logPrefix || this.logPrefix;
    
    if (!ydoc) {
      return {
        valid: false,
        message: 'No Y.js document provided'
      };
    }

    try {
      const yxml = this.getXmlFragment(ydoc, 'prosemirror', options);
      if (!yxml) {
        return {
          valid: false,
          message: 'Cannot access prosemirror XML fragment'
        };
      }

      log.log(`${logPrefix} Document validation successful`, {
        windowId: options.windowId,
        docId: options.docId
      });

      return {
        valid: true,
        message: 'Document is valid and accessible'
      };
    } catch (error) {
      log.error(`${logPrefix} Document validation failed:`, error);
      return {
        valid: false,
        message: `Document validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Get document state from application state or EditorView
   */
  public getDocumentFromApplicationState(
    applicationState: any,
    options: YDocumentAccessOptions = {}
  ): { ydoc: Y.Doc | null; editorView: EditorView | null } {
    const logPrefix = options.logPrefix || this.logPrefix;
    
    try {
      const docsState = applicationState?.docs;
      if (!docsState) {
        log.warn(`${logPrefix} No docs state in application state`);
        return { ydoc: null, editorView: null };
      }

      const ydoc = docsState.ydoc || null;
      const editorView = docsState.editorView || null;

      log.log(`${logPrefix} Retrieved document from application state:`, {
        hasYdoc: !!ydoc,
        hasEditorView: !!editorView,
        windowId: options.windowId,
        docId: options.docId
      });

      return { ydoc, editorView };
    } catch (error) {
      log.error(`${logPrefix} Error retrieving document from application state:`, error);
      return { ydoc: null, editorView: null };
    }
  }
}

// Export singleton instance
export const yDocumentService = YDocumentService.getInstance(); 